import React, { useState } from 'react';
import cn from 'clsx';
import PropTypes from 'prop-types';

import { CheckboxGroup } from '../../../../library/checkbox/checkbox';
import Search from '../../../../library/search/search';
import { MinusLightIcon, PlusLightIcon } from '../../../../shared/icons/icons';
import CategoryCollapsible from '../category-collapsible/category-collapsible';
import FilterOption from '../filter-option/filter-option';

import { FILTER_CATEGORY_CODES } from '../../../../shared/constants/filters';

import styles from './option-category.scss';

const ALLOWED_CHARS = /[a-z ]$/i;
const SEARCH_MAX_LENGTH = 25;
const DEFAULT_REGEX = /.*/;
const SHOW_MORE_SIZE = 5;

function OptionCategory(props) {
  const {
    buttonId = '',
    categoryName,
    children,
    description,
    filterValues,
    hasTextFiltering = false,
    isOpenByDefault = false,
    onClick,
    onToggleShowMore,
    selectedFilters,
    showMoreByDefault = false,
    textFilteringPlaceholder = 'Search Brand...',
    toggleType
  } = props;

  const [isShowingMore, setIsShowingMore] = useState(
    Boolean(showMoreByDefault)
  );
  const [searchFieldText, setSearchFieldText] = useState('');
  const [filterRegex, setFilterRegex] = useState(DEFAULT_REGEX);
  const filteredFilterValues = filterValues.filter(
    value =>
      typeof value.valueName === 'string' &&
      typeof value.categoryName === 'string' &&
      typeof value.categoryCode === 'string' &&
      typeof value.valueCode === 'string' &&
      value?.categoryCode !== FILTER_CATEGORY_CODES.FLEET_PREFERRED_PRODUCTS &&
      filterRegex.test(value.valueName)
  );
  const filterValuesToRender =
    filteredFilterValues.length > SHOW_MORE_SIZE && !isShowingMore
      ? filterValues.slice(0, SHOW_MORE_SIZE)
      : filteredFilterValues;

  function toggleShowMore() {
    setIsShowingMore(!isShowingMore);
    onToggleShowMore(buttonId);
  }

  function updateFilterRegex(value) {
    if (value && value.length >= 1) {
      setFilterRegex(new RegExp(value, 'i'));
    } else if (!value) {
      setFilterRegex(DEFAULT_REGEX);
    }
  }

  function checkLength(event) {
    if (event.target.value.length >= SEARCH_MAX_LENGTH) {
      event.preventDefault();
    }
  }

  function updateShowMore(event) {
    const { value } = event.target;

    if (ALLOWED_CHARS.test(value) || !value) {
      setSearchFieldText(value);
      updateFilterRegex(value);
    }
  }

  function isSelectedFilter(filterValue) {
    if (!Array.isArray(selectedFilters)) {
      return false;
    }

    return Boolean(
      selectedFilters.find(
        selectedFilter =>
          selectedFilter.valueCode === filterValue.valueCode &&
          selectedFilter.categoryCode === filterValue.categoryCode
      )
    );
  }

  if (
    (!filterValuesToRender || filterValuesToRender.length === 0) &&
    filterRegex === DEFAULT_REGEX
  ) {
    return false;
  }

  return (
    <CategoryCollapsible
      className="option-category"
      description={description}
      isOpenByDefault={isOpenByDefault}
      label={categoryName}
      toggleType={toggleType}
    >
      <ul className={cn(styles['options'], 'option-category__options')}>
        {hasTextFiltering && (
          <li className={styles['option']}>
            <Search
              inputMode="search"
              maxLength={SEARCH_MAX_LENGTH}
              onCancelClick={val => {
                if (!val) {
                  updateFilterRegex('');
                }
                setSearchFieldText(val);
              }}
              onChange={updateShowMore}
              onKeyPress={checkLength}
              placeholder={textFilteringPlaceholder || ''}
              value={searchFieldText}
            />
          </li>
        )}

        <CheckboxGroup groupTitle={categoryName}>
          {filterValuesToRender.map((filterValue, index) => (
            <FilterOption
              categoryCode={filterValue.categoryCode}
              categoryName={filterValue.categoryName}
              isSelected={isSelectedFilter(filterValue)}
              key={`filteroption-${filterValue.valueCode}-${index}`}
              label={
                typeof children === 'function' ? children(filterValue) : null
              }
              onClick={typeof onClick === 'function' ? onClick : null}
              valueCode={filterValue.valueCode}
              valueName={filterValue.valueName}
            />
          ))}
        </CheckboxGroup>

        {filteredFilterValues.length > SHOW_MORE_SIZE && (
          <li className={styles['option']}>
            <button
              className="option-category__show-more-button"
              id={buttonId}
              onClick={toggleShowMore}
              type="button"
            >
              {isShowingMore ? (
                <>
                  Show Less <MinusLightIcon />
                </>
              ) : (
                <>
                  Show More <PlusLightIcon />
                </>
              )}
            </button>
          </li>
        )}
      </ul>
    </CategoryCollapsible>
  );
}

OptionCategory.propTypes = {
  buttonId: PropTypes.string,
  categoryName: PropTypes.string,
  children: PropTypes.func,
  description: PropTypes.string,
  filterValues: PropTypes.arrayOf(
    PropTypes.shape({
      categoryCode: PropTypes.string,
      categoryName: PropTypes.string,
      valueCode: PropTypes.string,
      valueName: PropTypes.string
    })
  ),
  hasTextFiltering: PropTypes.bool,
  isOpenByDefault: PropTypes.bool,
  onClick: PropTypes.func,
  onToggleShowMore: PropTypes.func,
  selectedFilters: PropTypes.array,
  showMoreByDefault: PropTypes.bool,
  textFilteringPlaceholder: PropTypes.string,
  toggleType: PropTypes.string
};

export default OptionCategory;
